import { uiLocale } from '../shared/chart.utils';

/** Shared view-model helpers of the read-only KNX device sub-tabs (lights, sockets, blinds). */

/** Anything placed by the HomeServer's location tree (floor and room are optional). */
export interface KnxPlaced {
  floor: string | null;
  room:  string | null;
}

/** One room card. `room` is null for devices placed on a floor only (e.g. "Außen": Balkon, Terrasse)
 *  and for unplaced devices (floor null as well). */
export interface KnxRoomGroup<T> {
  key:   string;
  room:  string | null;
  items: T[];
}

/** One floor section; `floor` is null for the trailing group of unplaced devices ("Ohne Raum"). */
export interface KnxFloorGroup<T, R extends KnxRoomGroup<T> = KnxRoomGroup<T>> {
  key:   string;
  floor: string | null;
  rooms: R[];
}

/**
 * Floor → room grouping that keeps the backend's location order (floors E0…E5, "Außen", rooms in
 * HomeServer order, unplaced devices last): groups appear in the order of their first device.
 */
export function groupByLocation<T extends KnxPlaced>(items: T[]): KnxFloorGroup<T>[] {
  const floors = new Map<string, { floor: string | null; rooms: Map<string, KnxRoomGroup<T>> }>();
  for (const it of items) {
    const fKey = it.floor ?? '';
    let f = floors.get(fKey);
    if (!f) floors.set(fKey, f = { floor: it.floor, rooms: new Map() });
    const rKey = it.room ?? '';
    let r = f.rooms.get(rKey);
    if (!r) f.rooms.set(rKey, r = { key: `${fKey}|${rKey}`, room: it.room, items: [] });
    r.items.push(it);
  }
  const out: KnxFloorGroup<T>[] = [];
  for (const [key, f] of floors) out.push({ key, floor: f.floor, rooms: [...f.rooms.values()] });
  // Unplaced devices last, even if the backend order ever changes.
  const i = out.findIndex(g => g.floor === null);
  if (i >= 0 && i < out.length - 1) out.push(...out.splice(i, 1));
  return out;
}

/** Formats "since" timestamps in the UI locale: "18:42" today, "Mo. 18:42" / "Mon 18:42" within the last
 *  week, "12.10. 18:42" / "12/10 18:42" before. Build a new one after a language switch. */
export class KnxSinceFormatter {
  private readonly time:    Intl.DateTimeFormat;
  private readonly weekday: Intl.DateTimeFormat;
  private readonly date:    Intl.DateTimeFormat;
  private readonly full:    Intl.DateTimeFormat;
  private readonly today:   string;

  constructor(private readonly now = Date.now(), locale = uiLocale()) {
    this.time    = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
    this.weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    this.date    = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' });
    this.full    = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
    this.today   = new Date(now).toDateString();
  }

  short(ts: number): string {
    const d = new Date(ts);
    const time = this.time.format(d);
    if (d.toDateString() === this.today) return time;
    if (this.now - ts < 6 * 24 * 3600 * 1000) return `${this.weekday.format(d)} ${time}`;
    return `${this.date.format(d)} ${time}`;
  }

  long(ts: number): string {
    return this.full.format(new Date(ts));
  }
}

/** Percentage rounded for display and clamped to 0..100; null for a missing / non-finite value. */
export function pct(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(100, Math.round(v))) : null;
}

/** Phones: below 576 px (styles.css). */
export const PHONE_QUERY = '(max-width: 575.98px)';
/** The app's mobile view: below 768 px the shell shows one tab at a time with the mobile tab bar (also landscape phones). */
export const MOBILE_VIEW_QUERY = '(max-width: 767.98px)';

/**
 * Follows a width breakpoint (phones by default). The KNX sub-tabs render compact list rows instead of card
 * grids in the whole mobile view (MOBILE_VIEW_QUERY), so the list starts where the shell's mobile tab bar
 * does. Rendered with *ngIf, not hidden with CSS, so a room's popup (one [linkId]) and its charts exist only
 * once. `matches` follows the breakpoint (the listener runs in Angular's zone, so the template switches on
 * its own; `changed` e.g. rebuilds chart options); call `destroy()` in ngOnDestroy.
 */
export class PhoneMedia {
  private readonly mql: MediaQueryList | null;
  matches: boolean;
  private readonly onChange = (e: MediaQueryListEvent) => { this.matches = e.matches; this.changed?.(e.matches); };

  constructor(private readonly changed?: (matches: boolean) => void, query = PHONE_QUERY) {
    this.mql = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null;
    this.matches = this.mql?.matches ?? false;
    this.mql?.addEventListener('change', this.onChange);
  }

  destroy(): void {
    this.mql?.removeEventListener('change', this.onChange);
  }
}

/** Persisted per-browser boolean flag (filter toggles); storage may be unavailable. */
export function loadFlag(key: string): boolean {
  try { return localStorage.getItem(key) === 'true'; } catch { return false; }
}

export function saveFlag(key: string, value: boolean): void {
  try { localStorage.setItem(key, String(value)); } catch { /* storage unavailable */ }
}
